import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationPanel = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadNotifications();
      checkPushPermission();
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to real-time notifications
    const unsubscribe = notificationService?.subscribeToNotifications(user?.id, (newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
      
      // Show browser push notification
      if (pushEnabled) {
        notificationService?.showBrowserNotification(newNotification?.title, {
          body: newNotification?.message,
          tag: newNotification?.id,
          requireInteraction: newNotification?.severity === 'error'
        });
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.id, pushEnabled]);

  const checkPushPermission = () => {
    const status = notificationService?.getPushPermissionStatus();
    setPushEnabled(status === 'granted');
  };

  const handleEnablePush = async () => {
    const granted = await notificationService?.requestPushPermission();
    setPushEnabled(granted);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService?.getAll(user?.id);
      setNotifications(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId, user?.id);
      setNotifications(notifications?.map(n => 
        n?.id === notificationId ? { ...n, isRead: true } : n
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService?.markAllAsRead(user?.id);
      setNotifications(notifications?.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'error':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'AlertCircle'
        };
      case 'warning':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'AlertTriangle'
        };
      case 'success':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle'
        };
      default:
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          icon: 'Info'
        };
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'card_purchase':
        return 'Card Purchase';
      case 'suspicious_activity':
        return 'Suspicious Activity';
      case 'spending_threshold':
        return 'Spending Threshold';
      default:
        return 'Notification';
    }
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : filter === 'unread'
    ? notifications?.filter(n => !n?.isRead)
    : notifications?.filter(n => n?.type === filter);

  if (!isOpen) return null;

  return (
    <div className="absolute top-full right-0 mt-2 w-96 max-h-[600px] bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Push Notification Toggle */}
        {notificationService?.getPushPermissionStatus() !== 'unsupported' && (
          <div className="mb-3 p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">Browser Notifications</span>
              </div>
              {pushEnabled ? (
                <span className="text-xs text-success font-medium">Enabled</span>
              ) : (
                <button
                  onClick={handleEnablePush}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Enable
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {['all', 'unread', 'card_purchase', 'suspicious_activity', 'spending_threshold']?.map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-smooth ${
                filter === filterType
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {filterType === 'all' ? 'All' : filterType === 'unread' ? 'Unread' : getTypeLabel(filterType)}
            </button>
          ))}
        </div>
        {notifications?.filter(n => !n?.isRead)?.length > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="mt-3 text-sm text-primary hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="overflow-y-auto max-h-[450px] scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-error">{error}</div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Bell" size={48} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications?.map((notification) => {
              const config = getSeverityConfig(notification?.severity);
              return (
                <div
                  key={notification?.id}
                  onClick={() => !notification?.isRead && handleMarkAsRead(notification?.id)}
                  className={`p-4 hover:bg-muted/30 transition-smooth cursor-pointer ${
                    !notification?.isRead ? 'bg-muted/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${config?.bgColor}`}>
                      <Icon name={config?.icon} size={16} className={config?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${config?.color} uppercase`}>
                          {getTypeLabel(notification?.type)}
                        </span>
                        {!notification?.isRead && (
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        {notification?.title}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {notification?.message}
                      </p>
                      {notification?.amount && (
                        <p className="text-sm font-semibold text-foreground mb-1">
                          ${notification?.amount?.toFixed(2)}
                          {notification?.paymentMethod && (
                            <span className="text-muted-foreground font-normal ml-2">
                              via {notification?.paymentMethod}
                            </span>
                          )}
                        </p>
                      )}
                      {notification?.merchant && (
                        <p className="text-xs text-muted-foreground mb-1">
                          Merchant: {notification?.merchant}
                        </p>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification?.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;